// client/src/hooks/usePublicProfileData.js

import { useState, useEffect, useContext, useCallback } from "react";
import AuthContext from "../context/AuthContext";
import { authGet, authPost } from "../Utilities/Api"; // Assuming you have authPost for actions

export const usePublicProfileData = (username) => {
    const { user } = useContext(AuthContext);

    // --- Profile Data States ---
    const [profile, setProfile] = useState(null);
    const [currentUserProfile, setCurrentUserProfile] = useState(null);
    const [notFound, setNotFound] = useState(false);

    // --- Relationship States ---
    const [alreadyFriends, setAlreadyFriends] = useState(false);
    const [requestSent, setRequestSent] = useState(false);
    const [isBlocked, setIsBlocked] = useState(false);
    const [incomingRequest, setIncomingRequest] = useState(false);

    // --- Mutual Friends States ---
    const [mutualFriends, setMutualFriends] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    // 1. Load viewed user's profile and reset all dependent states
    useEffect(() => {
        let alive = true;
        setNotFound(false);
        setProfile(null);

        // 🟢 ENHANCEMENT 1: Reset all relationship states when viewing a new profile
        setPage(1);
        setMutualFriends([]);
        setAlreadyFriends(false);
        setRequestSent(false);
        setIsBlocked(false);
        setIncomingRequest(false);

        const fetchProfile = async () => {
            if (!username) return;

            try {
                const res = await authGet(`/users/username/${encodeURIComponent(username)}`);
                if (!res.ok) {
                    if (alive) setNotFound(true);
                    return;
                }
                const data = await res.json().catch(() => null);
                if (alive) setProfile(data);
            } catch (err) {
                console.error("❌ Error loading profile:", err);
                if (alive) setNotFound(true);
            }
        };
        fetchProfile();

        return () => { alive = false; };
    }, [username]);

    // 2. Load current user's profile (for friendship state)
    useEffect(() => {
        if (!user) return;
        let alive = true;
        const fetchCurrentUserProfile = async () => {
            try {
                // Fetch full profile data, including relationship arrays
                const res = await authGet("/users/profile");
                const data = await res.json().catch(() => null);
                if (alive) setCurrentUserProfile(data);
            } catch (err) {
                console.error("❌ Error loading current user profile:", err);
            }
        };
        fetchCurrentUserProfile();
        return () => { alive = false; };
    }, [user]);

    // 3. Derive relationship flags
    useEffect(() => {
        if (!currentUserProfile || !profile || user?._id === profile?._id) return;

        const { friends, sentRequests, blockedUsers, incomingRequests } = currentUserProfile;

        // Ensure friends is an array of objects
        const isFriend = friends?.some(friend => friend._id === profile._id);

        setAlreadyFriends(Boolean(isFriend));

        // Assuming these are arrays of user ID strings:
        setRequestSent(Boolean(sentRequests?.includes(profile._id)));
        setIsBlocked(Boolean(blockedUsers?.includes(profile._id)));
        setIncomingRequest(Boolean(incomingRequests?.includes(profile._id)));
    }, [currentUserProfile, profile, user]);

    // 4. Mutual friends loader (stable function)
    const fetchMutualFriends = useCallback(async () => {
        if (!user || !profile || user._id === profile._id) return;
        try {
            const res = await authGet(`/friends/mutual/${profile._id}?page=${page}&limit=5`);
            const data = await res.json().catch(() => ({}));

            // Assuming this is for pagination where new results replace old ones
            setMutualFriends(data.mutualFriends || []);
            setTotalPages(data.totalPages || 1);
        } catch (err) {
            console.error("❌ Mutual friends error:", err);
        }
    }, [user, profile, page]); // Dependencies are correct

    // 5. Trigger mutual friends fetch when profile or page changes
    useEffect(() => {
        fetchMutualFriends();
    }, [fetchMutualFriends]); // Triggered when 'profile' or 'page' changes

    // --- FRIENDSHIP ACTION WRAPPERS (Optional, but highly recommended) ---
    // These abstract the API logic and update local state immediately for fast UI feedback
    const sendFriendRequest = useCallback(async () => {
        if (requestSent || alreadyFriends || !profile) return;
        try {
            await authPost(`/friends/request/${profile._id}`);
            setRequestSent(true); // Optimistic UI update
        } catch (err) {
            console.error("❌ Failed to send friend request:", err);
        }
    }, [requestSent, alreadyFriends, profile]);

    const handleAcceptRequest = useCallback(async () => {
        if (!incomingRequest || !profile) return;
        try {
            await authPost(`/friends/accept/${profile._id}`);
            setIncomingRequest(false);
            setAlreadyFriends(true); // Optimistic UI update
        } catch (err) {
            console.error("❌ Failed to accept request:", err);
        }
    }, [incomingRequest, profile]);

    // --- Return all state and actions ---
    return {
        // Data
        profile,
        notFound,
        isOwnProfile: user && profile && user._id === profile._id,
        mutualFriends,
        page,
        totalPages,

        // Status Flags
        alreadyFriends,
        requestSent,
        isBlocked,
        incomingRequest,

        // Actions
        setPage,
        // 🟢 ENHANCEMENT: Expose the state-updating actions
        sendFriendRequest,
        handleAcceptRequest,
        // Expose direct setters for use in other action handlers (e.g., Unfriend/Block)
        setRequestSent,
        setAlreadyFriends,
        setIncomingRequest,
        setIsBlocked,
    };
};